import { useEffect, useRef, useState } from "react";
import {
  claveDeRequest,
  fetchDashboardMetrics,
  type DashboardRequest,
  type DashboardResponse,
} from "../api/dashboard.api";

/**
 * Los tres estados que un widget puede mostrar, y que tienen que verse distinto.
 *
 * `vacio` no es un error y `sin-calcular` no es un cero: si los tres se dibujan igual, un operador
 * mira un tablero de ceros y no tiene forma de saber si es que no vendió nada, si el cron todavía no
 * corrió, o si se cayó la request.
 */
export type EstadoDeCarga = "cargando" | "listo" | "error";

export interface DashboardData {
  estado: EstadoDeCarga;
  data: DashboardResponse | null;
  error: string | null;
  recargar: () => void;
}

/**
 * Caché a nivel módulo, con TTL.
 *
 * Existe por una razón concreta del panel: los widgets están paginados de a seis, y sin caché
 * pasar a la página 2 y volver a la 1 dispara la misma request otra vez. El TTL se alinea con el
 * cron que materializa los agregados —cada 5 minutos—: pedir más seguido no puede traer un número
 * distinto.
 */
const TTL_MS = 5 * 60_000;
const cache = new Map<string, { at: number; data: DashboardResponse }>();

/** Para los tests, y para forzar recarga cuando el operador la pide explícitamente. */
export const limpiarCacheDeDashboard = () => cache.clear();

export function useDashboardMetrics(
  request: DashboardRequest | null,
): DashboardData {
  const [estado, setEstado] = useState<EstadoDeCarga>("cargando");
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Sin widgets no hay request: el operador apagó todo. No es carga ni error.
  const clave = request && request.widgets.length ? claveDeRequest(request) : null;
  const ultimaClave = useRef<string | null>(null);

  useEffect(() => {
    if (!clave || !request) {
      setEstado("listo");
      setData(null);
      return;
    }

    const enCache = cache.get(clave);
    if (enCache && Date.now() - enCache.at < TTL_MS && nonce === 0) {
      setData(enCache.data);
      setEstado("listo");
      setError(null);
      ultimaClave.current = clave;
      return;
    }

    const ac = new AbortController();
    // Sólo se muestra "cargando" si cambió lo que se pide. Al refrescar lo mismo, el panel se queda
    // con los números viejos en pantalla en vez de parpadear a vacío.
    if (ultimaClave.current !== clave) setEstado("cargando");
    setError(null);

    fetchDashboardMetrics(request, ac.signal)
      .then((r) => {
        cache.set(clave, { at: Date.now(), data: r });
        setData(r);
        setEstado("listo");
        ultimaClave.current = clave;
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        const msg =
          e?.response?.data?.message ??
          e?.message ??
          "No se pudieron cargar las métricas";
        setError(Array.isArray(msg) ? msg.join(", ") : String(msg));
        setEstado("error");
      });

    return () => ac.abort();
  }, [clave, nonce]);

  return {
    estado,
    data,
    error,
    recargar: () => {
      if (clave) cache.delete(clave);
      setNonce((n) => n + 1);
    },
  };
}
